'use client';
import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { loadGoogleApiSdk, loadGoogleIdentityServicesSdk, } from '#pplx/frontend/components/loadGoogleSdk.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLockBodyScroll } from 'react-use';
const SCOPES = [
    'https://www.googleapis.com/auth/userinfo.profile',
    'https://www.googleapis.com/auth/userinfo.email',
    'https://www.googleapis.com/auth/drive.readonly',
    'https://www.googleapis.com/auth/drive.metadata.readonly',
];
const SOURCE_MIME_TYPES = [
    'application/vnd.google-apps.file',
    'application/vnd.google-apps.folder',
    'application/vnd.google-apps.document', // google doc
    'application/vnd.google-apps.presentation', // google slide
    'application/vnd.google-apps.spreadsheet', // google sheet
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'text/plain', // .txt
    'application/pdf', // .pdf
    'text/csv', // .csv
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'text/markdown', // .md
];
const ATTACHMENT_MIME_TYPES = [
    'text/plain', // .txt
    'application/pdf', // .pdf
    'text/csv', // .csv
    'text/markdown', // .md
    'image/png', // .png
    'image/jpeg', // .jpeg, .jpg
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.google-apps.document', // google doc
    'application/vnd.google-apps.presentation', // google slide
    'application/vnd.google-apps.spreadsheet', // google sheet
];
const AV_MIME_TYPES = [
    'audio/mpeg',
    'audio/wav',
    'audio/aiff',
    'audio/ogg',
    'audio/flac',
    'audio/mp3',
    'video/mp4',
    'video/mpeg',
    'video/mov',
    'video/avi',
    'video/x-flv',
    'video/mpg',
    'video/webm',
    'video/wmv',
    'video/3gpp',
    'video/quicktime',
];
const GOOGLE_IDENTITY_PROMPT_REUSE_SESSION = 'none';
const GOOGLE_IDENTITY_PROMPT_GOOGLE_DECIDES = '';
const FAILED_TO_OPEN_PICKER_TOAST_TIMEOUT_SECONDS = 5;
const RETRYABLE_GOOGLE_IDENTITY_CALLBACK_ERRORS = new Set([
    'access_denied',
    'consent_required',
    'immediate_failed',
    'interaction_required',
    'login_required',
]);
const RETRYABLE_GOOGLE_IDENTITY_POPUP_ERRORS = new Set(['popup_closed', 'popup_failed_to_open']);
const shouldLetGoogleDecidePrompt = (options) => options.promptBehavior === 'google_decides' || Boolean(options.scopes?.length);
const getGoogleIdentityPopupErrorType = (error) => {
    const errorType = error?.type;
    return typeof errorType === 'string' ? errorType : null;
};
const shouldRetryGoogleIdentityRequest = ({ error, hasRetriedWithGooglePrompt, prompt, }) => {
    if (prompt !== GOOGLE_IDENTITY_PROMPT_REUSE_SESSION || hasRetriedWithGooglePrompt) {
        return false;
    }
    if (typeof error === 'string') {
        return RETRYABLE_GOOGLE_IDENTITY_CALLBACK_ERRORS.has(error);
    }
    const errorType = getGoogleIdentityPopupErrorType(error);
    return errorType !== null && RETRYABLE_GOOGLE_IDENTITY_POPUP_ERRORS.has(errorType);
};
export const useGoogleDrivePicker = ({ isAudioVideoFilesEnabled, onError, }) => {
    const [isPickerOpen, setPickerOpen] = useState(false);
    const { openToast } = useToastContext();
    const { $t } = useIntl();
    useLockBodyScroll(isPickerOpen);
    const handleTokenRequestFailure = useCallback((reason, error) => {
        openToast({
            message: $t({
                defaultMessage: 'Failed to open Google Drive file picker. If this problem persists, please try reconnecting your Google Drive account.',
                id: 'f8a8000492',
                description: 'Toast notification in the use Google Drive Picker shared utilities section',
            }),
            variant: 'error',
            timeoutSeconds: FAILED_TO_OPEN_PICKER_TOAST_TIMEOUT_SECONDS,
        });
        logger.warn('[Google Drive Picker] Access token request failed.', {
            reason,
            error,
        });
        onError?.();
    }, [onError, openToast, $t]);
    const createPicker = useCallback((accessToken, options) => {
        const pickerAPI = window.google?.picker;
        const pickerCallback = (data) => {
            if (data.action === pickerAPI.Action.PICKED) {
                setPickerOpen(false);
                if (options.onPicked) {
                    options.onPicked(data.docs ?? EMPTY_ARRAY);
                }
            }
            else if (data.action === pickerAPI.Action.CANCEL) {
                setPickerOpen(false);
                options.onCancel?.();
            }
            else if (data.action === pickerAPI.Action.ERROR) {
                setPickerOpen(false);
                onError?.();
            }
        };
        if (pickerAPI) {
            try {
                if (!options.apiKey) {
                    openToast({
                        message: $t({
                            defaultMessage: 'Failed to open Google Drive file picker',
                            id: 'b36a6cdf25',
                            description: 'Toast notification in the use Google Drive Picker shared utilities section',
                        }),
                        variant: 'error',
                        timeoutSeconds: FAILED_TO_OPEN_PICKER_TOAST_TIMEOUT_SECONDS,
                    });
                    logger.error('[Google Drive Picker] Missing API key; aborting picker open.', {
                        hasClientId: Boolean(options.clientId),
                    });
                    onError?.();
                    return;
                }
                const createDocsView = () => new pickerAPI.DocsView()
                    .setIncludeFolders(true)
                    .setSelectFolderEnabled(true)
                    .setMode(pickerAPI.DocsViewMode.LIST);
                const myDriveView = createDocsView();
                myDriveView.setParent('root');
                const view_group = new pickerAPI.ViewGroup(myDriveView)
                    .addView(createDocsView().setOwnedByMe(false))
                    .addView(createDocsView().setStarred(true))
                    .addView(createDocsView().setEnableDrives(true));
                const pickerBuilder = new pickerAPI.PickerBuilder()
                    .setSize(1051, 650)
                    .addViewGroup(view_group)
                    .setOAuthToken(accessToken)
                    .setCallback(pickerCallback);
                if (options.mode !== 'trigger_scope') {
                    const modeMimeTypes = options.mode === 'source' ? SOURCE_MIME_TYPES : ATTACHMENT_MIME_TYPES;
                    const selectableMimeTypes = isAudioVideoFilesEnabled
                        ? modeMimeTypes.concat(AV_MIME_TYPES)
                        : modeMimeTypes;
                    pickerBuilder.setSelectableMimeTypes(selectableMimeTypes.join(','));
                }
                if (options.multiselect !== false) {
                    pickerBuilder.enableFeature(pickerAPI.Feature.MULTISELECT_ENABLED);
                }
                pickerBuilder.setDeveloperKey(options.apiKey);
                const picker = pickerBuilder.build();
                picker.setVisible(true);
                setPickerOpen(true);
            }
            catch {
                onError?.();
            }
        }
    }, [isAudioVideoFilesEnabled, onError, openToast, $t]);
    const openPicker = useCallback(async (options) => {
        try {
            await Promise.all([loadGoogleApiSdk(), loadGoogleIdentityServicesSdk()]);
        }
        catch {
            onError?.();
            return;
        }
        let hasRetriedWithGooglePrompt = false;
        const scope = (options.scopes?.length ? options.scopes : SCOPES).join(' ');
        const initialPrompt = shouldLetGoogleDecidePrompt(options)
            ? GOOGLE_IDENTITY_PROMPT_GOOGLE_DECIDES
            : GOOGLE_IDENTITY_PROMPT_REUSE_SESSION;
        // These functions intentionally reference each other: a silent GIS request can
        // fall back to an interactive request, while the retry shares terminal handling.
        function retryWithGooglePrompt(prompt, error) {
            if (!shouldRetryGoogleIdentityRequest({
                error,
                hasRetriedWithGooglePrompt,
                prompt,
            })) {
                return false;
            }
            hasRetriedWithGooglePrompt = true;
            // On the silent prompt, access_denied can mean Google needs user interaction.
            // If the interactive retry is denied, it reaches terminal error handling.
            requestAccessToken(GOOGLE_IDENTITY_PROMPT_GOOGLE_DECIDES);
            return true;
        }
        function requestAccessToken(prompt) {
            let hasRequestSettled = false;
            const settleRequest = () => {
                if (hasRequestSettled) {
                    return false;
                }
                hasRequestSettled = true;
                return true;
            };
            const tokenClient = window.google.accounts.oauth2.initTokenClient({
                client_id: options.clientId,
                login_hint: options.loginHint,
                scope,
                prompt,
                error_callback: (error) => {
                    if (!settleRequest()) {
                        return;
                    }
                    if (retryWithGooglePrompt(prompt, error)) {
                        return;
                    }
                    if (getGoogleIdentityPopupErrorType(error) === 'popup_closed' && options.onCancel) {
                        options.onCancel();
                        return;
                    }
                    handleTokenRequestFailure('popup_error', error);
                },
                callback: (response) => {
                    if (!settleRequest()) {
                        return;
                    }
                    if (response.error !== undefined) {
                        if (retryWithGooglePrompt(prompt, response.error)) {
                            return;
                        }
                        handleTokenRequestFailure('callback_error', response.error);
                        return;
                    }
                    if (!response.access_token) {
                        handleTokenRequestFailure('missing_access_token');
                        return;
                    }
                    createPicker(response.access_token, options);
                },
            });
            tokenClient.requestAccessToken();
        }
        requestAccessToken(initialPrompt);
    }, [createPicker, handleTokenRequestFailure, onError]);
    return useMemo(() => ({ openPicker }), [openPicker]);
};
//# sourceMappingURL=useGoogleDrivePicker.js.map